import { apiDownloadUrlGpx, apiDownloadUrlKml, apiKey, apiUrl } from "@/config";
import { LRUCache } from "lru-cache";

const cache = new LRUCache({ max: 500 });

/**
 * Basic fetch method for Outdooractive
 *
 * @param path
 * @param params
 * @param options ttl is the cache time in seconds. 0 = infinity
 * @returns
 */
async function fetchFromOA<OAResponse>(
  path: string,
  params?: Record<string, string | undefined>
): Promise<OAResponse> {
  const url = new URL(path, apiUrl);
  url.searchParams.set("key", apiKey);
  url.searchParams.set("lang", "de");

  if (params) {
    Object.entries(params).forEach(
      ([key, value]) => value !== undefined && url.searchParams.set(key, value)
    );
  }

  const cacheKey = url.toString();
  const cachedResponse = cache.get(cacheKey) as OAResponse;

  if (cachedResponse) {
    return Promise.resolve(cachedResponse);
  }

  const response = await fetch(url, {
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching from data api: ${response.status} ${response.statusText}`,
      { cause: response }
    );
  }

  const data = await response.json();
  cache.set(cacheKey, data);

  return data;
}

export async function fetchCategoryTree(type: Travelguide.OOI.Type) {
  const { category } = await fetchFromOA<{
    category: Travelguide.Outdooractive.CategoryData[];
  }>(`category/tree/${type}/pruned`, undefined);
  return category;
}

export async function fetchOOIs<
  FetchResponse extends Record<
    Travelguide.OOI.Type,
    Travelguide.Outdooractive.POIData
  > &
    Partial<{
      poi: Array<Travelguide.Outdooractive.POIData>;
      tour: Array<Travelguide.Outdooractive.TourData>;
      event: Array<Travelguide.Outdooractive.EventData>;
      region: Array<Travelguide.Outdooractive.POIData>;
    }>,
>(id: Travelguide.ID | Array<Travelguide.ID>) {
  const ids = Array.isArray(id) ? id : [id];
  if (ids.length == 0) {
    throw new Error("No ids given", { cause: { status: 400 } });
  }

  const params = Array.isArray(id) ? { display: "list" } : {};
  const oois = await fetchFromOA<FetchResponse>(
    `oois/${ids.join(",")}`,
    params
  );

  if (Object.keys(oois).length === 0) {
    throw new Error("Not found", { cause: { status: 404 } });
  }

  return oois;
}

export function fetchFilter({
  type,
  categories,
}: {
  type: Travelguide.OOI.Type;
  categories: Array<Travelguide.ID>;
}) {
  return categories.map((category) =>
    fetchFromOA<{ data: Array<{ id: Travelguide.ID }> }>(`filter/${type}`, {
      category,
    })
  );
}

export function fetchDownload({
  format,
  id,
}: {
  format: "gpx" | "kml";
  id: Travelguide.ID;
}) {
  const url = new URL(format === "gpx" ? apiDownloadUrlGpx : apiDownloadUrlKml);
  url.searchParams.set("key", apiKey);
  url.searchParams.set("i", id);
  return fetch(url);
}
